import { useRecoilValueLoadable } from "recoil";
import { iconState } from "../states/components/IconStates";
import {memo, useEffect, useState} from "react";

const Icon = ({
	name,
	color = "purple-semi-dark",
	filled = true,
	strokeThickness = null,
	style = {},
	...props
}) => {
	const iconResponse = useRecoilValueLoadable(iconState(name));
	// We use this state and useEffect to match server side rendering and client side rendering.
	const [icon, setIcon] = useState({state: 'hasValue', contents: null});

	useEffect(() => {
		setIcon(iconResponse)
	}, [iconResponse])

	if (icon.state === "loading") {
		return <i className="icon-loading" title="Loading icon"/>;
	}

	if (icon.state === "hasError" || (icon.state === "hasValue" && icon.contents === null)) {
		return <i className="icon-error" title={`Error loading ${name} icon`}/>;
	}

	if (icon.contents === null) {
		return null;
	}

	return (
		<svg
			ref={(el) => {
				if (el) {
					// Copy all SVG attributes
					[...icon.contents.attributes].forEach((attr) => {
						el.setAttribute(attr.nodeName, attr.nodeValue);
					});

					// Copy SVG code
					const iconSVG = icon.contents;

					// Add missing viewbox
					if (!iconSVG.getAttribute("viewBox")) {
						const iconHeight = iconSVG.getAttribute("height");
						const iconWidth = iconSVG.getAttribute("width");
						iconSVG.setAttribute("viewBox", `0 0 ${iconWidth} ${iconHeight}`);
					}

					// Copy SVG content
					el.innerHTML = iconSVG.innerHTML;


					// Copy SVG attributes
					Array.from(iconSVG.attributes).forEach((attr) => {
						el.setAttribute(attr.nodeName, attr.nodeValue);
					});

					// For each prop, lets add to the <svg> also
					for (var prop in props) {
						if (prop === "className") {
							el.setAttribute("class", "");
							props[prop].split(" ").forEach((className) => {
								if (typeof el.classList !== "undefined" && className.length > 0) {
									el.classList.add(className);
								}
							});
							continue;
						}

						if (prop.match(/^on[A-Z].+$/)) {
							let event = prop.replace(/^on([A-Z].+)$/, "$1");
							event = event.charAt(0).toLowerCase() + event.slice(1);
							el[`on${event}`] = props[prop];
							el.setAttribute(`data-${event}-attached`, true);
							continue;
						}

						el.setAttribute(prop, props[prop]);
					}

					// Add icon name to class
					el.classList.add(`icon-${name}`);
					el.classList.add("icon");

					// Get dimensions if available in SVG (should be)
					let width = el.attributes.getNamedItem("width");
					width = width ? width.value : null;
					let height = el.attributes.getNamedItem("height");
					height = height ? height.value : null;

					// If given only width, calculate the proportionate height
					if (props.width && !props.height && height && width) {
						const heightRatio = width / height;
						el.setAttribute("height", parseInt(props.width / heightRatio));
					}
					// If given only height, calculate the proportionate width
					if (props.height && !props.width && height && width) {
						const heightRatio = width / height;
						el.setAttribute("width", parseInt(props.height * heightRatio));
					}

					// Add fill color class
					if (color && typeof el.classList !== "undefined") {
						el.classList.forEach((className) => {
							if (className.match(/^fill-[a-zA-Z-]+$/)) {
								el.classList.remove(className);
							}
						});
						el.classList.forEach((className) => {
							if (className.match(/^stroke-[a-zA-Z-]+$/)) {
								el.classList.remove(className);
							}
						});
						el.classList.add(`fill-${color}`);
						if (el.lastChild.classList) {
							el.lastChild.classList.add(`stroke-${color}`);
						}

						if (filled) {
							el.classList.remove(`fill-as-outline`);
							if (el.lastChild.classList) {
								el.lastChild.classList.add(`fill-${color}`);
							}
						} else {
							el.classList.add(`fill-as-outline`);
							if (el.lastChild.classList) {
								el.lastChild.classList.remove(`fill-${color}`);
							}
						}
					}

					// Add style
					Object.assign(el.style, style);
				}
			}}
		/>
	);
};

export default memo(Icon);
